import cloneDeep from 'lodash/cloneDeep'
import { v4 as uuid4 } from 'uuid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  BuilderComponentSettingValue,
  BuilderDroppableElement,
  BuilderMenu,
  ConditionFeatureFlag,
  ElementSetting,
  IActionType,
  TableAdapter,
  TableColumnListColumnValue,
  TableColumnListValue,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'

import { IAppLayer } from '@/core/app-builder/app/layer'
import {
  BuilderActionSetting,
  BuilderComponent,
  BuilderComponentSetting,
  BuilderComponentSettingType,
  BuilderComponentSettingValidation,
  ComponentBehaviorKey,
  ComponentSettingKey,
} from '@/shared/builder'
import { ChartComponents } from '@/shared/builder/components/charts.group'
import { ListComponents } from '@/shared/builder/components/lists.group'
import {
  getDefaultColorAdaptiveSettings,
  getDefaultResponsiveSettings,
  hasColorAdaptiveSettings,
  hasResponsiveSettings,
} from '@/shared/builder/viewport/responsive-settings'
import { getOperatorDefaultOption } from '@/shared/condition'

const DEFAULT_THUMBNAILS = ['/assets/img/default_thumbnail_1.png', '/assets/img/default_thumbnail_2.png']

const EXCEPTION_IDS = [
  ComponentSettingKey.ButtonDataLabel,
  ComponentSettingKey.ImageDataTitle,
  ComponentSettingKey.ImageDataLike,
  ComponentSettingKey.ImageDataTag,
  ComponentSettingKey.VideoDataCover,
  ComponentSettingKey.ArrayGalleryDataCompTitle,
  ComponentSettingKey.AlertDataTitle,
  ComponentSettingKey.AlertDataDescription,
  ComponentSettingKey.SelectionDataHelp,
  ComponentSettingKey.SliderDataHelp,
  ComponentSettingKey.FileUploadDataHelp,
  ComponentSettingKey.ImageUploadDataHelp,
  ComponentSettingKey.AudioDataCover,
  ComponentSettingKey.DateTimePickerDataLabel,
  ComponentSettingKey.SliderDataLabel,
]

const getRandomThumbnail = () => {
  const randomIndex = Math.floor(Math.random() * DEFAULT_THUMBNAILS.length)
  return DEFAULT_THUMBNAILS[randomIndex]
}

/**
 * parse default image
 */
function parseDefaultImage(settingKey: ComponentSettingKey, tempSetting: ElementSetting, defaultValue: string) {
  tempSetting[settingKey] = {
    isLocal: true,
    value: defaultValue,
  }
}

/**
 * parse default data filter
 */
function parseDefaultDataFilter(
  settingKey: ComponentSettingKey,
  tempSetting: ElementSetting,
  builderContext: {
    tables: TableAdapter[]
    menus: BuilderMenu[]
    flattened: BuilderComponentSetting[]
  },
  tableSourceId: string
) {
  const table = builderContext.tables?.find((tb) => tb.id === tableSourceId)
  const colOptions = (table?.columns ?? []).filter(
    (c) =>
      ![
        ColumnTypes.Connection,
        ColumnTypes.RichText,
        ColumnTypes.RowID,
        ColumnTypes.Icon,
        ColumnTypes.Address,
        ColumnTypes.Slug,
        ColumnTypes.Array,
        ColumnTypes.QRCode,
        ColumnTypes.ImageArray,
        ColumnTypes.Barcode,
        ColumnTypes.AISEO,
        ColumnTypes.AITextProcessing,
      ].includes(c.type)
  )

  if (table) {
    tempSetting[settingKey] = {
      isAny: false,
      conditions: [
        {
          lhs: colOptions[0]?.id,
          operator: getOperatorDefaultOption(colOptions[0], table, builderContext.tables),
          caseSensitive: true,
          userSpecific: false,
          rhs: undefined,
        },
      ],
    }
  }
}

/**
 * parse component action setting
 */
function parseCompActionSetting(
  tempSetting: ElementSetting,
  actions: BuilderActionSetting[],
  builderContext: {
    menus: BuilderMenu[]
  },
  detailTableId?: string,
  compId?: string
) {
  for (const item of actions) {
    if (item.defaultValue) {
      if (!Array.isArray(item.defaultValue)) {
        if (
          [IActionType.ShowContextualMenu, IActionType.OpenMenu].includes(item.defaultValue.type) &&
          typeof item.defaultValue.settings === 'object' &&
          item.defaultValue.settings !== null
        ) {
          item.defaultValue.settings.value = builderContext.menus?.[0]?.id
        }
      }
      tempSetting[item.id] = item.defaultValue
    } else {
      if (!item.isSimple) {
        tempSetting[item.id] = []
      }
    }
  }
}

/**
 * Helper function to get role column ID from profile table
 */
function getProfileRoleColumnId(tables: TableAdapter[]): string | undefined {
  const appUserTableID = window.appUserTableID
  const userTable = tables.find((t) => t.id === appUserTableID)
  if (!userTable) return undefined

  // Try to get role column from user table
  // First, try to find column with name 'role' (case insensitive)
  let roleColumn = userTable.columns.find((col) => {
    return col.name?.toLowerCase() === 'role'
  })

  // If not found by name, try to find by checking if column has role attribute
  // Note: TableAdapter might have _UserTableAttr property on columns
  if (!roleColumn) {
    roleColumn = userTable.columns.find((col: any) => {
      return col._UserTableAttr === 'role' || col._UserTableAttr === 4 // ColumnUserTableAttributes.Role
    })
  }

  return roleColumn?.id
}

/**
 * Resolve dynamic column references in visibility default value
 * Replaces 'PROFILE_ROLE' placeholder with actual role column ID
 */
function resolveVisibilityDefaultValue(
  defaultValue: { isAny: boolean; conditions: any[] },
  tables: TableAdapter[]
): { isAny: boolean; conditions: any[] } {
  const roleColumnId = getProfileRoleColumnId(tables)

  if (!roleColumnId) {
    // If role column not found, return as is
    return defaultValue
  }

  // Deep clone to avoid mutating original
  const resolved = {
    isAny: defaultValue.isAny,
    conditions: defaultValue.conditions.map((condition) => {
      // Check if lhs has ProfileData flag and col is 'PROFILE_ROLE'
      if (condition.lhs?.flag === ConditionFeatureFlag.ProfileData && condition.lhs?.col === 'PROFILE_ROLE') {
        return {
          ...condition,
          lhs: {
            ...condition.lhs,
            col: roleColumnId,
          },
        }
      }
      return condition
    }),
  }

  return resolved
}

/**
 * parse component item setting
 */
function parseCompItemSetting(
  tempSetting: ElementSetting,
  items: BuilderComponentSetting[],
  comp: BuilderComponent,
  builderContext: {
    tables: TableAdapter[]
    menus: BuilderMenu[]
    flattened: BuilderComponentSetting[]
    layer?: IAppLayer // only used for form settings for now
  },
  tblId?: string,
  parentFormElId?: string
) {
  const isImage = comp.compId === 'image' && !comp.isForm

  const isListOrChart =
    comp.compId &&
    (ListComponents.components.some((c) => c.compId === comp.compId) ||
      ChartComponents.components.some((c) => c.compId === comp.compId))

  for (const item of items) {
    if (item.type === BuilderComponentSettingType.Group) {
      const items = item.groupItems ? Object.values(item.groupItems) : []
      if (items.length) {
        for (const groupItem of items) {
          parseCompItemSetting(tempSetting, groupItem, comp, builderContext, tblId, parentFormElId)
        }
      }
    }
    // parse default settings for every breakpoints
    let isDesign = hasResponsiveSettings(item.id)
    if (isDesign) {
      tempSetting[item.id] = getDefaultResponsiveSettings(item.id)
    } else if (item.defaultValue) {
      if (hasColorAdaptiveSettings(item.id)) {
        tempSetting[item.id] = getDefaultColorAdaptiveSettings(item.id)
      } else {
        // if (item.id === ComponentSettingKey.DateTimePickerDataLabel) return
        tempSetting[item.id] = parseDefaultValue(
          item.type,
          item.defaultValue,
          item.iconSelectSettings?.multiple ??
            item.selectSettings?.multiple ??
            item.variableSelectSettings?.multiple ??
            item.iconSelectSettings?.multiple ??
            item.screenSelectSettings?.multiple ??
            item.menuSelectSettings?.multiple,
          tblId ?? ''
        )

        if (item.type === BuilderComponentSettingType.Visibility && tempSetting[item.id]) {
          tempSetting[item.id] = resolveVisibilityDefaultValue(
            tempSetting[item.id] as { isAny: boolean; conditions: any[] },
            builderContext.tables
          )
        }
      }
    }

    if (tblId && !isListOrChart && item.autoDefaultInDetailPage !== false) {
      if (EXCEPTION_IDS.includes(item.id as ComponentSettingKey)) return

      if (item.type === BuilderComponentSettingType.VariableText) {
        const tbl = builderContext.tables.find((tb) => tb.id === tblId)
        const columnTypes = item.variableTextSettings?.columnTypes || [ColumnTypes.Text]

        // sort columns by based on columnTypes order
        const filteredColumns = (tbl?.columns || []).sort((a, b) => {
          const aIndex = columnTypes.indexOf(a.type)
          const bIndex = columnTypes.indexOf(b.type)
          return aIndex - bIndex
        })

        const col = filteredColumns.find((c) => {
          if (item.validation) {
            if (item.validation === BuilderComponentSettingValidation.URL) {
              return c.type === ColumnTypes.URL
            } else if (item.validation === BuilderComponentSettingValidation.Number) {
              return c.type === ColumnTypes.Number
            } else if (
              item.validation === BuilderComponentSettingValidation.AudioFile ||
              item.validation === BuilderComponentSettingValidation.VideoFile
            ) {
              return c.type === ColumnTypes.Attachment
            } else {
              return columnTypes.includes(c.type)
            }
          } else {
            return columnTypes.includes(c.type)
          }
        })

        if (col) {
          tempSetting[item.id] = [
            {
              type: 'paragraph',
              children: [
                {
                  text: '',
                },
                {
                  type: VariableTextFeatureFlag.CurrentRow,
                  id: uuid4(),
                  col: col.id,
                  userSpecific: false,
                  children: [
                    {
                      text: '',
                    },
                  ],
                },
                {
                  text: '',
                },
              ],
            },
          ]
        }
      }
      if (item.type === BuilderComponentSettingType.VariableSelect) {
        const tbl = builderContext.tables.find((tb) => tb.id === tblId)

        let columnTypes = item.variableSelectSettings?.columnTypes || [ColumnTypes.Text]

        if (item.id === ComponentSettingKey.AudioDataCover || item.id === ComponentSettingKey.VideoDataCover) {
          columnTypes = [ColumnTypes.Image]
        }

        if (isImage) {
          columnTypes = [ColumnTypes.Image]
          if (item.id === ComponentSettingKey.ImageDataLike) {
            columnTypes = [ColumnTypes.Boolean]
          }
        }

        // sort columns by based on columnTypes order
        const filteredColumns = (tbl?.columns ?? []).sort((a, b) => {
          const aIndex = columnTypes.indexOf(a.type)
          const bIndex = columnTypes.indexOf(b.type)
          return aIndex - bIndex
        })
        const col = filteredColumns.find((c) => columnTypes.includes(c.type))

        if (typeof col !== 'undefined') {
          tempSetting[item.id] = item.variableSelectSettings?.multiple
            ? [
                {
                  id: uuid4(),
                  flag: VariableSelectFeatureFlag.CurrentRow,
                  col: col.id,
                  userSpecific: false,
                },
              ]
            : {
                id: uuid4(),
                flag: VariableSelectFeatureFlag.CurrentRow,
                col: col.id,
                userSpecific: false,
              }
        }
      }
      if (item.type === BuilderComponentSettingType.TableColumnList) {
        const tbl = builderContext.tables.find((tb) => tb.id === tblId)
        const cols = (tbl?.columns || [])
          .filter((c) =>
            [
              ColumnTypes.Text,
              ColumnTypes.Number,
              ColumnTypes.Boolean,
              ColumnTypes.Icon,
              ColumnTypes.DateTime,
            ].includes(c.type)
          )
          .map((c) => {
            return {
              id: uuid4(),
              label: c.name,
              col: c.id,
              userSpecific: false,
              icon: undefined,
            }
          }) as TableColumnListColumnValue[]
        if (tbl && cols) {
          tempSetting[item.id] = {
            table: tbl.id,
            isDetailPageTable: true,
            columns: cols,
          } as TableColumnListValue
        }
      }
      if (item.type === BuilderComponentSettingType.ColumnSelect) {
        const tbl = builderContext.tables.find((tb) => tb.id === tblId)

        const columnTypes = item.columnSelectSettings?.columnTypes || []

        // sort columns by based on columnTypes order
        const filteredColumns = (tbl?.columns ?? []).sort((a, b) => {
          const aIndex = columnTypes.indexOf(a.type)
          const bIndex = columnTypes.indexOf(b.type)
          return aIndex - bIndex
        })
        const col = filteredColumns.find((c) => columnTypes.includes(c.type))

        if (typeof col !== 'undefined') {
          tempSetting[item.id] = col.id
        }
      }
    } else {
      if (item.type === BuilderComponentSettingType.TableColumnList) {
        const defaultTable = builderContext.tables.find((table) => !table.isUserTable)
        if (defaultTable) {
          const columns = defaultTable.columns.slice(0, 3).map((c) => ({
            col: c.id,
            id: c.id,
            label: c.name,
            userSpecific: false,
          }))
          tempSetting[item.id] = { table: defaultTable.id, columns }
        }
      }
    }

    if (item.type === BuilderComponentSettingType.MenuSelect) {
      if (Boolean(item.menuSelectSettings?.autoDefault)) {
        tempSetting[item.id] = builderContext.menus?.[0]?.id
      }
    }

    if (item.type === BuilderComponentSettingType.DataTableColumnList) {
      const defaultTable = tblId
        ? builderContext.tables.find((table) => table.id === tblId)
        : builderContext.tables.find((table) => !table.isUserTable)

      const selectAllColumn = item.selectIcon && !item.showDeviceVisibility
      const maxColumn = selectAllColumn ? defaultTable?.columns.length : 3

      if (defaultTable) {
        const columns = defaultTable.columns.slice(0, maxColumn).map((c) => ({
          col: c.id,
          id: c.id,
          label: c.name,
          userSpecific: false,
          visibility: {
            phone: true,
            'phone-landscape': true,
            tablet: true,
            'tablet-landscape': true,
            desktop: true,
            tv: true,
          },
          icon: 'star',
        }))

        tempSetting[item.id] = {
          table: defaultTable.id,
          columns,
          isDetailPageTable: !!tblId,
        }
      }
    }

    if (
      [BuilderComponentSettingType.TableSelect, BuilderComponentSettingType.TableSelectWithColumns].includes(item.type)
    ) {
      let defaultTable = builderContext.tables.filter((table) => !table.isUserTable)[0]

      tempSetting[item.id] = parseDefaultValue(item.type, item.defaultValue || '', false, defaultTable.id)

      if (isListOrChart) {
        let defaultTableForList = builderContext.tables
          .filter((table) => !table.isUserTable)
          .find((tb) => !!tb.columns.find((col) => col.type.includes(ColumnTypes.Image))?.id)

        if (!!defaultTableForList?.id) {
          tempSetting[item.id] = defaultTableForList?.id
          defaultTable = defaultTableForList
        }
      }

      if (defaultTable && defaultTable.columns.length > 0) {
        for (const fs of builderContext.flattened.filter(
          (s) =>
            s.type === BuilderComponentSettingType.ColumnSelect &&
            s.columnSelectSettings?.noAutoSelect !== true &&
            s.columnSelectSettings?.tableSourceId === item.id
        )) {
          const isMultiple = fs.columnSelectSettings?.multiple
          // Determine the appropriate column types based on the fs.id condition
          let columnTypes = fs.columnSelectSettings?.columnTypes || [ColumnTypes.Text]

          if (
            [
              ComponentSettingKey.CardDataMedia,
              ComponentSettingKey.ListDataMedia,
              ComponentSettingKey.CardDataThumbnail,
              ComponentSettingKey.GalleryDataImage,
              ComponentSettingKey.GalleryDataAvatar,
            ].includes(fs.id as any)
          ) {
            const hasImageColumn = defaultTable.columns.some((c) => c.type === ColumnTypes.Image)
            columnTypes = hasImageColumn ? [ColumnTypes.Image] : [ColumnTypes.Icon]
          }

          // Find the column in defaultTable that matches the required column types
          const selectedColumn = defaultTable.columns.find((c) => columnTypes.includes(c.type))

          // Assign the found column's id to the tempSetting using fs.id as key
          if (selectedColumn) {
            tempSetting[fs.id] = isMultiple ? [selectedColumn.id] : selectedColumn.id
          }

          // for List component, generate supporting text with column other than title if possible
          if (comp.compId === 'list' && fs.id === ComponentSettingKey.ListDataText) {
            const titleCol = tempSetting[ComponentSettingKey.ListDataTitle] as string | undefined

            if (titleCol) {
              const filteredColumns = defaultTable.columns.filter((c) => c.id !== titleCol)
              const supportingTitleCol = filteredColumns.find((c) => columnTypes.includes(c.type))
              if (supportingTitleCol) {
                tempSetting[fs.id] = supportingTitleCol.id
              }
            }
          }
        }
      }

      // in details page, set form data source to detail table
      if (item.id === ComponentSettingKey.FormDataSource && tblId) {
        tempSetting[item.id] = tblId
      }
    }

    // parse default column for form elements, where the data is coming from
    // parent form element tableId
    if (comp.isForm && parentFormElId && builderContext.layer) {
      const formElSettings = builderContext.layer.componentsMap.get(parentFormElId)?.settings || {}
      const formTableSource = formElSettings[ComponentSettingKey.FormDataSource]
      const table = builderContext.tables.find((tb) => tb.id === formTableSource)

      // generate default column for form field
      if (item.columnSelectSettings?.noAutoSelect !== true && item.type === BuilderComponentSettingType.ColumnSelect) {
        if (table) {
          // find included column types
          const columnTypes = builderContext.flattened.find((i) => i.id === item.id)?.columnSelectSettings
            ?.columnTypes ?? [
            ColumnTypes.Text,
            ColumnTypes.Number,
            ColumnTypes.Address,
            ColumnTypes.URL,
            ColumnTypes.Email,
            ColumnTypes.Phone,
            ColumnTypes.Slug,
            ColumnTypes.Rating,
            ColumnTypes.Color,
            ColumnTypes.Slug,
            ColumnTypes.Array,
            ColumnTypes.ImageArray,
            ColumnTypes.Icon,
          ]

          const filteredColumns = (table.columns ?? []).sort((a, b) => {
            const aIndex = columnTypes.indexOf(a.type)
            const bIndex = columnTypes.indexOf(b.type)
            return aIndex - bIndex
          })

          // select first column
          const col = filteredColumns.find((c) => columnTypes.includes(c.type))
          if (col) {
            tempSetting[item.id] = col.id
          }
        }
      }

      // generate default label for form field
      if (item.id.includes('-data-label')) {
        const columnIdKey = Object.keys(tempSetting).find((key) => key.includes('-data-value'))

        if (columnIdKey) {
          const columnId = tempSetting[columnIdKey]
          const column = table?.columns.find((c) => c.id === columnId)
          if (!!column) {
            if (item.type === BuilderComponentSettingType.VariableText) {
              const text = item.id === ComponentSettingKey.DateTimePickerDataLabel ? 'Date & Time' : column.name
              tempSetting[item.id] = [
                {
                  type: 'paragraph',
                  children: [{ text }],
                },
              ]
            } else if (item.type === BuilderComponentSettingType.Text) {
              tempSetting[item.id] = column.name
            }
          }
        }
      }

      // // generate default icon for text field
      // if (item.id === ComponentSettingKey.TextFieldDataLeadingIcon) {
      //   const valueColumnId = tempSetting[ComponentSettingKey.TextFieldDataValue]
      //   const valueColumn = table?.columns.find((c) => c.id === valueColumnId)
      //   if (!!valueColumn) {
      //     tempSetting[item.id] = FieldTextColumnIconMap[valueColumn.type]
      //   }
      // }
    }
  }

  // add default image thumbnail (data src)
  if (isImage && !tempSetting[ComponentSettingKey.ImageDataSrc]) {
    parseDefaultImage(ComponentSettingKey.ImageDataSrc, tempSetting, getRandomThumbnail())
  }
}

/**
 * Utility function to generate (new component) settings,
 * it will use the BuilderComponent dictionary as reference based on the specified component structure.
 *
 * @see BuilderComponent
 */
export function parseCompSettings(
  builderComp: BuilderComponent,
  builderContext: {
    menus: BuilderMenu[]
    tables: TableAdapter[]
    flattened: BuilderComponentSetting[]
    layer?: IAppLayer
  },
  detailTableId?: string,
  parentElId?: string
) {
  // deep clone the builder component to prevent reference mutations
  // this will fix the issue where component settings defaultValue being changed by previous component
  const comp = cloneDeep(builderComp)

  const tempSetting: ElementSetting = {}

  if (comp.settings.dataGroup) {
    for (const group of Object.values(comp.settings.dataGroup)) {
      parseCompItemSetting(tempSetting, group.items, comp, builderContext, detailTableId, parentElId)
    }
  }

  if (comp.settings.data) {
    parseCompItemSetting(tempSetting, comp.settings.data, comp, builderContext, detailTableId, parentElId)
  }

  if (comp.settings.designGroup) {
    for (const group of Object.values(comp.settings.designGroup)) {
      parseCompItemSetting(tempSetting, group.items, comp, builderContext, detailTableId, parentElId)
    }
  }

  if (comp.settings.design) {
    parseCompItemSetting(tempSetting, comp.settings.design, comp, builderContext, detailTableId, parentElId)
  }

  if (comp.settings.action) {
    parseCompActionSetting(tempSetting, comp.settings.action, builderContext, detailTableId, parentElId)
  }

  if (comp.settings.behavior?.custom) {
    for (const group of Object.values(comp.settings.behavior.custom)) {
      parseCompItemSetting(tempSetting, group.items, comp, builderContext, detailTableId, parentElId)
    }
  }

  // Initialize visibility with default value if provided
  if (comp.settings.behavior?.visibility) {
    const visibilityConfig = comp.settings.behavior.visibility
    // Support both boolean (backward compatibility) and object format
    if (typeof visibilityConfig === 'object' && visibilityConfig.enabled && visibilityConfig.defaultValue) {
      // Resolve dynamic column references (e.g., PROFILE_ROLE)
      const resolvedDefaultValue = resolveVisibilityDefaultValue(visibilityConfig.defaultValue, builderContext.tables)
      tempSetting[ComponentBehaviorKey.Visibility] = resolvedDefaultValue
    }
  }

  return tempSetting
}

export function createBuilderElement(component: BuilderComponent, groupId: string): BuilderDroppableElement {
  return {
    id: uuid4(),
    compId: component.compId,
    icon: component.icon,
    groupId,
    children: [],
    nested: component.nested,
    title: component.title,
    customTitle: undefined,
    inactive: false,
    parentId: null,
    parentTitle: null,
    parentType: null,
  }
}

/**
 * parse default value for each component setting type
 */
export function parseDefaultValue(
  type: BuilderComponentSettingType,
  value: BuilderComponentSettingValue | BuilderComponentSettingValue[],
  multiple = false,
  defaultTableId?: string
): undefined | BuilderComponentSettingValue | BuilderComponentSettingValue[] {
  switch (type) {
    case BuilderComponentSettingType.Text:
      return value
    case BuilderComponentSettingType.Number:
      return Number(value)
    case BuilderComponentSettingType.NumberWithUnit:
      return typeof value === 'object' ? value : undefined
    case BuilderComponentSettingType.Switch:
      return Boolean(value)
    case BuilderComponentSettingType.ColorScheme:
    case BuilderComponentSettingType.Icon:
    case BuilderComponentSettingType.ColorVariant:
    case BuilderComponentSettingType.HorizontalAlignment:
    case BuilderComponentSettingType.VerticalAlignment:
      return typeof value === 'string' ? value : undefined
    case BuilderComponentSettingType.MenuSelect:
    case BuilderComponentSettingType.ScreenSelect:
    case BuilderComponentSettingType.IconSelect:
    case BuilderComponentSettingType.Select:
      return multiple ? (Array.isArray(value) ? value : undefined) : typeof value === 'string' ? value : undefined
    case BuilderComponentSettingType.Slider:
    case BuilderComponentSettingType.ColumnList:
    case BuilderComponentSettingType.UserColumnFilter:
    case BuilderComponentSettingType.GridColumns:
      return Array.isArray(value) ? value : undefined
    case BuilderComponentSettingType.DatePicker:
    case BuilderComponentSettingType.TimePicker:
      return value instanceof Date ? value : undefined
    case BuilderComponentSettingType.VariableSelect:
      if (multiple) {
        return Array.isArray(value) ? ((value[0] as Record<string, unknown>)?.flag ? value : undefined) : undefined
      } else {
        return (value as Record<string, unknown>).flag ? value : undefined
      }
    case BuilderComponentSettingType.VariableText:
      if (!Array.isArray(value)) {
        return undefined
      } else {
        if (!(value as Record<string, unknown>[])[0]?.type) {
          return undefined
        }
      }
      return value
    case BuilderComponentSettingType.TableSelect:
      return defaultTableId
    case BuilderComponentSettingType.TableSelectWithColumns:
      return {
        table: defaultTableId || '',
        columns: [],
      }
    case BuilderComponentSettingType.TableColumnList:
    case BuilderComponentSettingType.ColumnSelect:
    case BuilderComponentSettingType.Spacing:
    case BuilderComponentSettingType.TypographySelect:
    case BuilderComponentSettingType.Radius:
    case BuilderComponentSettingType.TypographyGroup:
    case BuilderComponentSettingType.ColumnFilter:
      return typeof value !== 'object' ? undefined : value
    case BuilderComponentSettingType.Divider:
      break
    default:
      return value
  }
}
